import React, { useEffect, useState, useMemo } from 'react';
import { Cloud, CloudOff, Loader2, Check } from 'lucide-react';

/**
 * SaveStateIndicator — Cloud-icon chip that reflects the autosave lifecycle.
 * Same visual pattern as the Grievance Builder wizard.
 */
export default function SaveStateIndicator({ state = 'idle', lastSavedAt }) {
  const [, force] = useState(0);

  // Re-render every 15s so "Xs ago" stays fresh without an explicit tick source.
  useEffect(() => {
    const t = setInterval(() => force((n) => n + 1), 15000);
    return () => clearInterval(t);
  }, []);

  const rel = useMemo(() => {
    if (!lastSavedAt) return '';
    const d = new Date(lastSavedAt);
    const s = Math.max(1, Math.round((Date.now() - d.getTime()) / 1000));
    if (s < 60)    return `${s}s ago`;
    if (s < 3600)  return `${Math.round(s / 60)}m ago`;
    return `${Math.round(s / 3600)}h ago`;
  }, [lastSavedAt]);

  const CFG = {
    idle:   { label: 'Not saved yet',    Icon: Cloud },
    dirty:  { label: 'Unsaved changes',  Icon: Cloud,   cls: 'jbv2-save-state--dirty' },
    saving: { label: 'Saving…',          Icon: Loader2, cls: 'jbv2-save-state--saving', spin: true },
    saved:  { label: `Saved · ${rel}`,   Icon: Check,   cls: 'jbv2-save-state--saved' },
    error:  { label: 'Save failed',      Icon: CloudOff, cls: 'jbv2-save-state--error' },
  };
  const cfg = CFG[state] || CFG.idle;

  return (
    <span
      className={`jbv2-save-state ${cfg.cls || ''}`}
      data-testid="jbv2-save-state"
      data-state={state}
      aria-live="polite"
    >
      <cfg.Icon size={13} className={cfg.spin ? 'jbv2-spin' : ''} />
      {cfg.label}
    </span>
  );
}
